import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Club } from '@/types/clubs';
import ClubHeader from '@/components/clubs/ClubHeader';
import ClubSidebar from '@/components/clubs/ClubSidebar';
import ClubAboutTab from '@/components/clubs/ClubAboutTab';
import ClubGroupChat from '@/components/clubs/chat/ClubGroupChat';
import { motion, AnimatePresence } from 'framer-motion';

const ClubDetails = () => {
  const { id: clubId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [club, setClub] = useState<Club | null>(null);
  const [loading, setLoading] = useState(true);
  const [joiningClub, setJoiningClub] = useState(false);
  const [activeTab, setActiveTab] = useState('about');

  console.log('ClubDetails rendered with clubId:', clubId);

  useEffect(() => {
    const fetchClubDetails = async () => {
      if (!clubId) {
        console.log('No clubId provided');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        console.log('Fetching club details for:', clubId);
        
        // Fetch club details
        const { data: clubData, error } = await supabase
          .from('clubs')
          .select('*')
          .eq('id', clubId)
          .single();
          
        if (error) {
          console.error('Error fetching club:', error);
          throw error;
        }
        
        console.log('Club data:', clubData);
        
        // Check if user is a member
        if (user) {
          console.log('Checking membership for user:', user.id);
          const { data: memberData } = await supabase
            .from('club_members')
            .select('*')
            .eq('club_id', clubId)
            .eq('user_id', user.id)
            .maybeSingle();
            
          console.log('Member data:', memberData);
            
          if (clubData) {
            const clubWithMembership = {
              ...clubData,
              is_member: !!memberData
            };
            console.log('Final club object:', clubWithMembership);
            setClub(clubWithMembership);
          }
        } else {
          setClub(clubData);
        }
      } catch (error) {
        console.error('Error fetching club details:', error);
        setClub(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchClubDetails();
  }, [clubId, user]);

  const handleJoinClub = async () => {
    if (!user || !clubId || !club) return;
    
    try {
      setJoiningClub(true);
      
      // Check if already a member
      const { data: existingMember } = await supabase
        .from('club_members')
        .select('*')
        .eq('club_id', clubId)
        .eq('user_id', user.id)
        .single();
        
      // If already a member, leave the club
      if (existingMember) {
        const { error } = await supabase
          .from('club_members')
          .delete()
          .eq('club_id', clubId)
          .eq('user_id', user.id);
          
        if (error) throw error;
        
        setClub({ ...club, is_member: false });
      } 
      // Otherwise join the club
      else {
        const { error } = await supabase
          .from('club_members')
          .insert({
            club_id: clubId,
            user_id: user.id,
            joined_at: new Date().toISOString()
          });
          
        if (error) throw error;
        
        setClub({ ...club, is_member: true });
      }
    } catch (error) {
      console.error('Error joining/leaving club:', error);
    } finally {
      setJoiningClub(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4 flex-1 flex items-center justify-center">
        <motion.div 
          className="animate-pulse flex flex-col items-center"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
        >
          <div className="h-8 bg-muted rounded w-40 mb-4"></div>
          <div className="h-64 bg-muted rounded w-full max-w-2xl"></div>
        </motion.div>
      </div>
    );
  }

  if (!club) {
    return (
      <motion.div 
        className="container mx-auto p-4 flex-1"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="text-center my-12">
          <motion.h2 
            className="text-2xl font-bold mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            Club Not Found
          </motion.h2>
          <motion.p 
            className="text-muted-foreground mb-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            The club you're looking for doesn't exist or has been removed.
          </motion.p>
        </div>
      </motion.div>
    );
  }

  console.log('Rendering ClubDetails with club:', club);

  return (
    <motion.div 
      className="container mx-auto p-4"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
    >
      <motion.div
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <ClubHeader
          club={club}
          joiningClub={joiningClub}
          onJoinClub={handleJoinClub}
        />
      </motion.div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Club Details */}
        <motion.div 
          className="lg:col-span-2"
          initial={{ x: -20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <Card className="overflow-hidden mb-6 shadow-lg hover:shadow-xl transition-all duration-300">
            <CardContent className="p-6">
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="mb-4 bg-muted/50 backdrop-blur-sm">
                  <TabsTrigger 
                    value="about"
                    className="transition-all duration-200 hover:bg-primary/10"
                  >
                    About
                  </TabsTrigger>
                  {club.is_member && (
                    <TabsTrigger 
                      value="chat"
                      className="transition-all duration-200 hover:bg-primary/10"
                    >
                      Group Chat
                    </TabsTrigger>
                  )}
                </TabsList>
                
                <AnimatePresence mode="wait">
                  <TabsContent value="about" className="mt-0">
                    <motion.div
                      key="about"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.3 }}
                    >
                      <ClubAboutTab description={club.description || ''} />
                    </motion.div>
                  </TabsContent>
                  
                  {club.is_member && (
                    <TabsContent value="chat" className="mt-0">
                      <motion.div
                        key="chat"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.3 }}
                      >
                        <ClubGroupChat 
                          clubId={clubId!} 
                          clubName={club.name}
                        />
                      </motion.div>
                    </TabsContent>
                  )}
                </AnimatePresence>
              </Tabs>
            </CardContent>
          </Card>
        </motion.div>
        
        {/* Sidebar */}
        <motion.div
          initial={{ x: 20, opacity: 0 }}
          animate={{ x: 0, opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <ClubSidebar club={club} />
        </motion.div>
      </div>
    </motion.div>
  );
};

export default ClubDetails;
